(function (global, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (global && global.document && !global.OdetteRecentlyViewed) {
    global.OdetteRecentlyViewed = api;
    api.start(global);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var STORAGE_KEY = 'odette:recently-viewed:v1';
  var MAX_HISTORY = 50;
  var MAX_DISPLAY = 12;
  var CARD_SECTION = 'ol-recently-viewed-card';

  function handle(value) {
    if (typeof value !== 'string') return '';
    value = value.trim().toLowerCase();
    // Real catalog handles include Unicode, including the trademark symbol.
    // Keep them as raw handles and encode exactly once when building a URL.
    if (!value || value.length > 255 || value === '.' || value === '..' || /[\s\u0000-\u001f\u007f/?#\\&%<>"']/.test(value)) return '';
    try { encodeURIComponent(value); } catch (_) { return ''; }
    return value;
  }

  function unique(values) {
    if (!Array.isArray(values)) return [];
    var seen = new Set();
    var result = [];
    for (var i = 0; i < values.length && result.length < MAX_HISTORY; i += 1) {
      var clean = handle(values[i]);
      if (clean && !seen.has(clean)) {
        seen.add(clean);
        result.push(clean);
      }
    }
    return result;
  }

  function parseHistory(raw) {
    if (typeof raw !== 'string' || raw.length > 50000) return [];
    try {
      var value = JSON.parse(raw);
      return value && value.version === 1 ? unique(value.handles) : [];
    } catch (_) {
      return [];
    }
  }

  function record(history, current) {
    current = handle(current);
    return current ? unique([current].concat(history)) : unique(history);
  }

  function candidates(history, current) {
    current = handle(current);
    return unique(history).filter(function (item) { return item !== current; });
  }

  function read(storage) {
    try {
      return { available: true, handles: parseHistory(storage.getItem(STORAGE_KEY)) };
    } catch (_) {
      return { available: false, handles: [] };
    }
  }

  function remember(storage, current) {
    var result = read(storage);
    if (!result.available) return result;
    if (!handle(current)) return result;
    result.handles = record(result.handles, current);
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, handles: result.handles }));
      return result;
    } catch (_) {
      return { available: false, handles: [] };
    }
  }

  function productView(locationHref, current, pageType) {
    current = handle(current);
    if (pageType !== 'product' || !current) return false;
    try {
      var url = new URL(locationHref);
      if (url.searchParams.has('section_id') || url.searchParams.has('sections')) return false;
      return decodeURIComponent(url.pathname).replace(/\/$/, '').endsWith('/products/' + current);
    } catch (_) {
      return false;
    }
  }

  function cardUrl(origin, rootUrl, productHandle) {
    var clean = handle(productHandle);
    if (!clean) throw new Error('Invalid product handle');
    var root = new URL(rootUrl || '/', origin);
    if (root.origin !== new URL(origin).origin || root.search || root.hash) throw new Error('Invalid storefront root');
    var url = new URL(root.pathname.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(clean), origin);
    url.searchParams.set('section_id', CARD_SECTION);
    return url.href;
  }

  async function loadCards(history, current, fetchCard, limit) {
    var queue = candidates(history, current);
    var cards = [];
    limit = Math.min(MAX_DISPLAY, Math.max(1, Number(limit) || MAX_DISPLAY));
    for (var i = 0; i < queue.length && cards.length < limit;) {
      var batch = queue.slice(i, i + Math.min(3, limit - cards.length));
      var results = await Promise.all(batch.map(async function (item) {
        try { return await fetchCard(item); } catch (_) { return null; }
      }));
      results.forEach(function (card) { if (card) cards.push(card); });
      i += batch.length;
    }
    return cards;
  }

  function start(win) {
    var doc = win.document;
    var observers = [];
    var generation = 0;
    var initialized = false;
    var config;

    function storage() {
      try { return win.localStorage; } catch (_) { return null; }
    }

    async function fetchCard(productHandle) {
      var controller = new win.AbortController();
      var timer = win.setTimeout(function () { controller.abort(); }, 7000);
      try {
        var rootUrl = win.Shopify && win.Shopify.routes && win.Shopify.routes.root || config.rootUrl;
        var url = cardUrl(win.location.origin, rootUrl, productHandle);
        var response = await win.fetch(url, {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal
        });
        if (!response.ok || (response.url && new URL(response.url).origin !== win.location.origin)) return null;
        var parsed = new win.DOMParser().parseFromString(await response.text(), 'text/html');
        var card = parsed.querySelector('[data-ol-recent-card]');
        if (!card || card.dataset.productHandle !== productHandle) return null;
        var link = card.querySelector('a.product-tile__link');
        if (!link) return null;
        var linkUrl = new URL(link.getAttribute('href'), win.location.origin);
        if (linkUrl.origin !== win.location.origin || !productView(linkUrl.href, productHandle, 'product')) return null;
        card.querySelectorAll('script').forEach(function (script) { script.remove(); });
        // The theme fades lazy images using a one-time observer. Dynamically fetched
        // cards must remain visible without depending on that earlier observer.
        card.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.classList.add('ol-geladen'); });
        return doc.importNode(card, true);
      } catch (_) {
        return null;
      } finally {
        win.clearTimeout(timer);
      }
    }

    function render(history) {
      generation += 1;
      var thisGeneration = generation;
      observers.forEach(function (observer) { observer.disconnect(); });
      observers = [];
      var current = handle(config.productHandle);
      doc.querySelectorAll('[data-ol-recently-viewed]').forEach(function (root) {
        var content = root.querySelector('[data-ol-recent-content]');
        var grid = root.querySelector('[data-ol-recent-grid]');
        var anchor = root.querySelector('[data-ol-recent-observer]');
        if (!content || !grid || !anchor) return;
        content.hidden = true;
        anchor.hidden = true;
        grid.replaceChildren();
        if (!candidates(history, current).length) return;
        anchor.hidden = false;

        async function populate() {
          var cards = await loadCards(history, current, fetchCard, MAX_DISPLAY);
          if (generation !== thisGeneration || !root.isConnected) return;
          anchor.hidden = true;
          if (!cards.length) return;
          grid.replaceChildren.apply(grid, cards);
          content.hidden = false;
        }

        if ('IntersectionObserver' in win) {
          var observer = new win.IntersectionObserver(function (entries) {
            if (entries.some(function (entry) { return entry.isIntersecting; })) {
              observer.disconnect();
              populate();
            }
          }, { rootMargin: '600px 0px' });
          observers.push(observer);
          observer.observe(anchor);
        } else {
          populate();
        }
      });
    }

    function activate(shouldRecord) {
      if (doc.visibilityState === 'hidden' || doc.prerendering || (win.Shopify && win.Shopify.designMode)) return;
      var current = shouldRecord && productView(win.location.href, config.productHandle, config.pageType) ? config.productHandle : '';
      var result = current ? remember(storage(), current) : read(storage());
      render(result.available ? result.handles : []);
      initialized = true;
    }

    function boot() {
      var node = doc.getElementById('ol-recently-viewed-config');
      if (!node) return;
      try { config = JSON.parse(node.textContent); } catch (_) { return; }
      if (!config || typeof config !== 'object') return;
      activate(true);
      // Another tab can add or clear history while this one is hidden. Refresh
      // when returning, without recording a second visit to an already open PDP.
      doc.addEventListener('visibilitychange', function () { activate(!initialized); });
      doc.addEventListener('prerenderingchange', function () { if (!initialized) activate(true); });
      win.addEventListener('pageshow', function (event) { if (event.persisted) activate(true); });
      win.addEventListener('storage', function (event) { if (event.key === STORAGE_KEY || event.key === null) activate(false); });
      doc.addEventListener('shopify:section:load', function () { activate(false); });
    }

    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
  }

  return { key: STORAGE_KEY, handle: handle, parseHistory: parseHistory, record: record, candidates: candidates, read: read, remember: remember, productView: productView, cardUrl: cardUrl, loadCards: loadCards, start: start };
});
